import React, { useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSearchCandidates } from "solostar-graphql";
import { Input, Button } from "solostar-components";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/solid";
import { useDebounce } from "use-debounce";

interface FilterState {
  query: string;
  name: string;
  company: string;
  jobTitle: string;
  education: string;
  location: string;
  industry: string;
}

const CandidateSearch: React.FC = () => {
  const navigate = useNavigate();
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 50;

  // Filter state
  const [filters, setFilters] = useState<FilterState>({
    query: "",
    name: "",
    company: "",
    jobTitle: "",
    education: "",
    location: "",
    industry: "",
  });

  // Debounce filters to avoid too many queries
  const [debouncedFilters] = useDebounce(filters, 500);

  // Build query input
  const queryInput = useMemo(() => {
    const input: any = {
      page,
      pageSize,
    };

    // Only include non-empty filters
    Object.keys(debouncedFilters).forEach((key) => {
      const value = debouncedFilters[key as keyof FilterState];
      if (value) {
        input[key] = value;
      }
    });

    return input;
  }, [debouncedFilters, page, pageSize]);

  // Fetch candidates
  const { data, loading, error } = useSearchCandidates(
    { input: queryInput },
    { skip: false }
  );

  const handleFilterChange = useCallback(
    (field: keyof FilterState) => (value: string) => {
      setFilters((prev) => ({ ...prev, [field]: value }));
      setPage(1); // Reset to first page on filter change
    },
    []
  );

  const handleClearFilters = useCallback(() => {
    setFilters({
      query: "",
      name: "",
      company: "",
      jobTitle: "",
      education: "",
      location: "",
      industry: "",
    });
    setPage(1);
  }, []);

  const handleCandidateClick = useCallback(
    (candidateId: string) => {
      navigate(`/ai/candidates/${candidateId}`);
    },
    [navigate]
  );

  const candidates = data?.candidates || [];
  const total = data?.total || 0;
  const hasMore = data?.hasMore || false;
  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">
            Candidate Search
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            Search and filter through candidate profiles
          </p>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          {/* Simple Search */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search
            </label>
            <Input
              value={filters.query}
              setValue={handleFilterChange("query")}
              placeholder="Search by name or company..."
              size="small"
            />
          </div>

          {/* Advanced Filters Toggle */}
          <div className="mb-4">
            <button
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className="text-sm text-indigo-600 hover:text-indigo-500 font-medium"
            >
              {showAdvancedFilters ? "Hide" : "Show"} Advanced Filters
            </button>
          </div>

          {/* Advanced Filters */}
          {showAdvancedFilters && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Name
                </label>
                <Input
                  value={filters.name}
                  setValue={handleFilterChange("name")}
                  placeholder="Filter by name..."
                  size="small"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company
                </label>
                <Input
                  value={filters.company}
                  setValue={handleFilterChange("company")}
                  placeholder="Filter by company..."
                  size="small"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Job Title
                </label>
                <Input
                  value={filters.jobTitle}
                  setValue={handleFilterChange("jobTitle")}
                  placeholder="Filter by job title..."
                  size="small"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Education
                </label>
                <Input
                  value={filters.education}
                  setValue={handleFilterChange("education")}
                  placeholder="Filter by school..."
                  size="small"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Location
                </label>
                <Input
                  value={filters.location}
                  setValue={handleFilterChange("location")}
                  placeholder="Filter by location..."
                  size="small"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Industry
                </label>
                <Input
                  value={filters.industry}
                  setValue={handleFilterChange("industry")}
                  placeholder="Filter by industry..."
                  size="small"
                />
              </div>
            </div>
          )}

          {/* Clear Filters */}
          <div className="flex justify-end">
            <Button onClick={handleClearFilters} size="small">
              Clear All Filters
            </Button>
          </div>
        </div>

        {/* Results Summary */}
        <div className="mb-4">
          <p className="text-sm text-gray-600">
            {loading ? (
              "Loading..."
            ) : error ? (
              <span className="text-red-600">
                {error.message || "Error loading candidates"}
              </span>
            ) : (
              <>
                Showing {candidates.length > 0 ? (page - 1) * pageSize + 1 : 0}{" "}
                - {Math.min(page * pageSize, total)} of {total} candidates
              </>
            )}
          </p>
        </div>

        {/* Results Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Headline
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Current Company
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Industry
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center">
                      <div className="flex justify-center items-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                      </div>
                    </td>
                  </tr>
                ) : candidates.length === 0 ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-6 py-12 text-center text-gray-500"
                    >
                      No candidates found. Try adjusting your filters.
                    </td>
                  </tr>
                ) : (
                  candidates.map((candidate: any, idx: number) => {
                    const currentPosition = candidate.positions?.[0];
                    const currentCompany = currentPosition?.source?.name || "-";

                    return (
                      <tr
                        key={candidate.id}
                        onClick={() => handleCandidateClick(candidate.id)}
                        className={`cursor-pointer hover:bg-gray-50 transition-colors ${
                          idx % 2 === 0 ? "bg-white" : "bg-gray-50"
                        }`}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {candidate.avatarUrl && (
                              <img
                                className="h-10 w-10 rounded-full mr-3"
                                src={candidate.avatarUrl}
                                alt={candidate.name}
                              />
                            )}
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {candidate.name}
                              </div>
                              {candidate.email && (
                                <div className="text-sm text-gray-500">
                                  {candidate.email}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900 max-w-xs truncate">
                            {candidate.headline || "-"}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {candidate.location || "-"}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {currentCompany}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {candidate.industry || "-"}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {!loading && candidates.length > 0 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 mt-4 rounded-lg shadow-sm">
            <div className="flex-1 flex justify-between sm:hidden">
              <Button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                size="small"
              >
                Previous
              </Button>
              <Button
                onClick={() => setPage((p) => p + 1)}
                disabled={!hasMore}
                size="small"
              >
                Next
              </Button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Page <span className="font-medium">{page}</span> of{" "}
                  <span className="font-medium">{totalPages}</span>
                </p>
              </div>
              <div>
                <nav
                  className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                  aria-label="Pagination"
                >
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Previous</span>
                    <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                  <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                    {page}
                  </span>
                  <button
                    onClick={() => setPage((p) => p + 1)}
                    disabled={!hasMore}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Next</span>
                    <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CandidateSearch;
